import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// Handles drag and drop for stats prioritization
export default class extends Controller {
  static targets = ["highPriority", "available", "input"]
  static values = { 
    stats: Array,
    maxHighPriority: { type: Number, default: 10 }
  }
  
  connect() {
    console.log("Stats priority controller connected")
    this.initializeSortable()
    this.updateHiddenInput()
  }
  
  initializeSortable() {
    // Check if targets exist
    if (!this.hasHighPriorityTarget || !this.hasAvailableTarget) {
      console.warn("Stats priority targets not found")
      return
    }
    
    // Options for both sortable containers
    const sortableOptions = {
      group: 'stats',
      animation: 150,
      ghostClass: 'opacity-50',
      chosenClass: 'shadow-lg',
      dragClass: 'cursor-grabbing',
      fallbackOnBody: true,
      swapThreshold: 0.65,
      // Mobile-friendly options
      forceFallback: true, // Better for touch devices
      fallbackTolerance: 3,
      touchStartThreshold: 5,
      // Handle events
      onEnd: (evt) => this.handleDragEnd(evt),
      onMove: (evt) => this.handleMove(evt)
    }
    
    // Initialize high priority container
    this.highPrioritySortable = Sortable.create(this.highPriorityTarget, {
      ...sortableOptions,
      onAdd: (evt) => this.handleAddToHighPriority(evt)
    })
    
    // Initialize available stats container
    this.availableSortable = Sortable.create(this.availableTarget, {
      ...sortableOptions,
      onAdd: (evt) => this.handleAddToAvailable(evt)
    })
    
    console.log("Sortable initialized for stats priority")
  }
  
  handleDragEnd(evt) {
    // Update the hidden input whenever drag ends
    this.updateHiddenInput()
    this.updateEmptyStates()
  }
  
  handleAddToHighPriority(evt) {
    // Check if we've exceeded max high priority stats
    const highPriorityCount = this.highPriorityTarget.querySelectorAll('.stat-item').length
    if (highPriorityCount > this.maxHighPriorityValue) {
      // Move the last item back to available
      const lastItem = this.highPriorityTarget.lastElementChild
      if (lastItem && lastItem !== evt.item) {
        this.availableTarget.appendChild(lastItem)
      }
    }
    this.updateHiddenInput()
  }
  
  handleAddToAvailable(evt) {
    this.updateHiddenInput()
  }
  
  handleMove(evt) {
    // Optional: Add visual feedback during move
    return true // Allow all moves
  }
  
  updateHiddenInput() {
    if (!this.hasInputTarget) {
      console.warn("No hidden input target found")
      return
    }
    
    // Get all stats from high priority container
    const highPriorityStats = Array.from(
      this.highPriorityTarget.querySelectorAll('.stat-item')
    ).map(item => item.dataset.stat).filter(stat => stat)
    
    // Update the hidden input
    this.inputTarget.value = JSON.stringify(highPriorityStats)
    
    console.log("Updated focused stats:", highPriorityStats)
    console.log("Hidden input value:", this.inputTarget.value)
    console.log("Hidden input name:", this.inputTarget.name)
    
    // Dispatch event for other components
    this.dispatch("statsUpdated", { 
      detail: { 
        stats: highPriorityStats,
        count: highPriorityStats.length
      } 
    })
  }
  
  updateEmptyStates() {
    // Show/hide empty state messages
    const highPriorityEmpty = this.highPriorityTarget.querySelectorAll('.stat-item').length === 0
    const availableEmpty = this.availableTarget.querySelectorAll('.stat-item').length === 0
    
    // Add empty state message if needed
    if (highPriorityEmpty && !this.highPriorityTarget.querySelector('.empty-state')) {
      const emptyState = document.createElement('div')
      emptyState.className = 'empty-state text-gray-400 text-center py-8'
      emptyState.textContent = 'Drag stats here to prioritize them'
      this.highPriorityTarget.appendChild(emptyState)
    } else if (!highPriorityEmpty) {
      const emptyState = this.highPriorityTarget.querySelector('.empty-state')
      if (emptyState) emptyState.remove()
    }
    
    if (availableEmpty && !this.availableTarget.querySelector('.empty-state')) {
      const emptyState = document.createElement('div')
      emptyState.className = 'empty-state text-gray-400 text-center py-8'
      emptyState.textContent = 'All stats are prioritized'
      this.availableTarget.appendChild(emptyState)
    } else if (!availableEmpty) {
      const emptyState = this.availableTarget.querySelector('.empty-state')
      if (emptyState) emptyState.remove()
    }
  }
  
  // Method to reset stats to default
  reset() {
    // Move all stats back to available
    const allStats = this.highPriorityTarget.querySelectorAll('.stat-item')
    allStats.forEach(stat => {
      this.availableTarget.appendChild(stat)
    })
    this.updateHiddenInput()
    this.updateEmptyStates()
  }
  
  disconnect() {
    // Clean up sortable instances
    if (this.highPrioritySortable) {
      this.highPrioritySortable.destroy()
    }
    if (this.availableSortable) {
      this.availableSortable.destroy()
    }
  }
}
